// src/components/blog/RelatedArticles.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleCard } from './ArticleCard';
import { ArticleMetadata } from '@dopomogai/types';

interface RelatedArticlesProps {
  /** The slug of the current article, to exclude it from the list. */
  currentArticleSlug: string;
  /** The category of the current article, to find related ones. */
  category: string;
  /** An array of all articles to filter from, assumed to be pre-sorted by date (newest first). */
  allArticles: ArticleMetadata[];
}

// The number of related articles you want to display.
const ARTICLES_TO_SHOW = 3;

export const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  currentArticleSlug,
  category,
  allArticles,
}) => {
  const { t } = useTranslation('blog');

  // useMemo will prevent this complex filtering from re-running on every render
  const related = useMemo(() => {
    // --- New, more intelligent logic ---

    // 1. Find other articles in the same category.
    const sameCategoryArticles = allArticles.filter(
      (article) =>
        article.category === category && article.slug !== currentArticleSlug
    );

    // 2. Create a Set of slugs we've already used to avoid duplicates.
    const excludeSlugs = new Set([currentArticleSlug, ...sameCategoryArticles.map(a => a.slug)]);
    
    // 3. Find the most recent articles that are NOT the current one and NOT already in our category list.
    const otherRecentArticles = allArticles.filter(article => !excludeSlugs.has(article.slug));

    // 4. Combine the lists: category matches first, then fill with recent articles.
    const combined = [...sameCategoryArticles, ...otherRecentArticles];
    
    // 5. Return the exact number of articles we want to show.
    return combined.slice(0, ARTICLES_TO_SHOW);

  }, [currentArticleSlug, category, allArticles]);


  // If, after all that, we have no articles to show, render nothing.
  if (related.length === 0) {
    return null;
  }
  
  return (
    <div>
      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
        {t('sections.relatedTitle', 'You Might Also Like...')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Render the final list of related articles */}
        {related.map((article, index) => (
          <ArticleCard key={article.slug} article={article} index={index} />
        ))}
      </div>
    </div>
  );
};